import React, { useEffect, useRef, useState } from 'react';
import { Input, Textarea } from '@ideoz/ui';
import type { ComputedStylePayload } from '../../preview-bridge/protocol';
import { askSmartPrompt, hotEditStore, type HotEditBridgeApi, type HotEditSelection } from '../../stores/hotEdit';
import { RADIUS_STEPS, radiusStepFor, usesRadiusToken, type RadiusStep } from '../../preview-bridge/designTokens';

/**
 * The panel's own sections (Prototype Editor UX plan, step 2), replacing the developer panel that
 * showed a font stack, tracking, opacity and every side of everything for any element. What is
 * left is what a founder asks for by name: the words, a form field's parts, and under More
 * options the exact numbers - size, spacing per side, corner radius and border width.
 *
 * Every control changes the preview at once and queues the change; Apply writes them.
 */

const FIELD = 'h-8 text-xs';
const LABEL = 'text-[11px] font-medium text-muted-foreground';

function px(raw: string | undefined): string {
  const s = (raw ?? '').trim();
  const m = /^([\d.]+)px$/i.exec(s);
  return m ? m[1]! : s.replace(/\s*px\s*$/i, '').trim();
}

/** The element's words. Nested markup (a line holding an icon) is left to Ask Ideoz. */
export function TextSection({
  selection,
  api,
}: {
  selection: HotEditSelection;
  api: HotEditBridgeApi;
}): React.ReactElement {
  const [draft, setDraft] = useState(selection.textContent ?? '');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (document.activeElement !== ref.current) setDraft(selection.textContent ?? '');
  }, [selection.elementId, selection.textContent]);

  if (selection.hasChildElements) {
    return (
      <p className="text-xs text-muted-foreground">
        This line mixes text and other things, so ask Ideoz to reword it.
      </p>
    );
  }

  return (
    <label className="block space-y-1.5">
      <span className={LABEL}>{selection.listRow ? 'Text of this item' : 'Text'}</span>
      <Textarea
        ref={ref}
        value={draft}
        className="min-h-[68px] text-xs"
        onChange={(e) => {
          setDraft(e.target.value);
          api.scheduleApplyInstantText(selection.elementId, e.target.value);
        }}
        onBlur={() => api.queuePersistText(draft)}
      />
    </label>
  );
}

/** What a form control shows: its value, its placeholder, its kind, a dropdown's options. */
export function FieldsSection({
  selection,
  api,
}: {
  selection: HotEditSelection;
  api: HotEditBridgeApi;
}): React.ReactElement {
  const tag = selection.tagName.toLowerCase();
  const [value, setValue] = useState(selection.inputValue ?? '');
  const [placeholder, setPlaceholder] = useState(selection.inputPlaceholder ?? '');
  const [options, setOptions] = useState((selection.optionValues ?? []).join('\n'));

  useEffect(() => {
    setValue(selection.inputValue ?? '');
    setPlaceholder(selection.inputPlaceholder ?? '');
    setOptions((selection.optionValues ?? []).join('\n'));
  }, [selection.elementId, selection.inputValue, selection.inputPlaceholder, selection.optionValues]);

  if (tag === 'select') {
    return (
      <label className="block space-y-1.5">
        <span className={LABEL}>Options, one per line</span>
        <Textarea
          value={options}
          className="min-h-[84px] text-xs font-mono"
          onChange={(e) => setOptions(e.target.value)}
          onBlur={() =>
            api.queuePersistSelectOptions(
              options
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean),
            )
          }
        />
      </label>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block space-y-1.5">
        <span className={LABEL}>Value</span>
        <Input
          className={FIELD}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            api.applyInstantDom(selection.elementId, 'value', e.target.value);
          }}
          onBlur={() => api.queuePersistDomValue(value)}
        />
      </label>
      <label className="block space-y-1.5">
        <span className={LABEL}>Placeholder</span>
        <Input
          className={FIELD}
          value={placeholder}
          onChange={(e) => {
            setPlaceholder(e.target.value);
            api.applyInstantDom(selection.elementId, 'placeholder', e.target.value);
          }}
          onBlur={() => api.queuePersistPlaceholder(placeholder)}
        />
      </label>
      {tag === 'input' ? (
        <label className="block space-y-1.5">
          <span className={LABEL}>Kind</span>
          <select
            className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs"
            value={selection.inputType ?? 'text'}
            onChange={(e) => {
              api.applyInstantDom(selection.elementId, 'type', e.target.value);
              api.queuePersistInputType(e.target.value);
            }}
          >
            {['text', 'email', 'password', 'number', 'search', 'tel', 'url', 'date'].map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

/** The exact numbers, for the founder who asks for them. Everything here is optional. */
export function MoreOptions({
  selection,
  api,
}: {
  selection: HotEditSelection;
  api: HotEditBridgeApi;
}): React.ReactElement {
  const computed = (selection.computedStyles ?? {}) as ComputedStylePayload;
  const push = (property: string, value: string) => {
    api.applyInstantCss(selection.elementId, property, value);
    api.bumpMergedFromTailwind(property, value);
    api.queuePersistTypographyLayout();
  };

  return (
    <div className="space-y-3">
      <Row label="Text size" unit="px" initial={px(computed.fontSize)} onCommit={(v) => push('fontSize', `${v}px`)} />
      <div className="space-y-1.5">
        <span className={LABEL}>Space inside</span>
        <div className="grid grid-cols-4 gap-1.5">
          {(
            [
              ['Top', 'paddingTop', computed.paddingTop],
              ['Right', 'paddingRight', computed.paddingRight],
              ['Bottom', 'paddingBottom', computed.paddingBottom],
              ['Left', 'paddingLeft', computed.paddingLeft],
            ] as const
          ).map(([side, property, value]) => (
            <Row key={side} label={side} unit="" initial={px(value)} onCommit={(v) => push(property, `${v}px`)} compact />
          ))}
        </div>
      </div>
      <RadiusSteps selection={selection} api={api} current={radiusStepFor(parseFloat(px(computed.borderRadius)) || 0)} />
      <Row
        label="Border width"
        unit="px"
        initial={px(computed.borderTopWidth)}
        onCommit={(v) => push('borderTopWidth', `${v}px`)}
      />
      <p className="text-[11px] text-muted-foreground">
        For anything else - a font, a layout, a new section - ask Ideoz below.
      </p>
    </div>
  );
}

/**
 * Corners, in the five steps the design system knows rather than in pixels. When the element's
 * corners come from the design system (`rounded-lg` follows `--radius`), a new step is a question
 * about the whole prototype, asked the same way a colour is (Prototype Editor UX plan, step 4).
 */
function RadiusSteps({
  selection,
  api,
  current,
}: {
  selection: HotEditSelection;
  api: HotEditBridgeApi;
  current: RadiusStep;
}): React.ReactElement {
  const [picked, setPicked] = useState<RadiusStep>(current);
  useEffect(() => setPicked(current), [current, selection.elementId]);

  const onlyHere = (value: string) => {
    api.bumpMergedFromTailwind('borderRadius', value);
    api.queuePersistTypographyLayout();
  };

  const choose = (step: (typeof RADIUS_STEPS)[number]) => {
    setPicked(step.step);
    api.applyInstantCss(selection.elementId, 'borderRadius', step.value);
    if (!usesRadiusToken(selection.className)) {
      onlyHere(step.value);
      return;
    }
    askSmartPrompt({
      title: `${step.label} corners everywhere?`,
      body: 'These corners come from the design system, so every card, button and field that shares them can follow.',
      broad: {
        label: 'Everywhere',
        run: () => {
          api.applyInstantCss(selection.elementId, 'borderRadius', '');
          api.previewThemeToken('radius', step.value);
          api.queueThemeToken('radius', step.value, 'Corner style');
        },
      },
      narrow: { label: 'Only this one', run: () => onlyHere(step.value) },
    });
  };

  return (
    <div className="space-y-1.5">
      <span className={LABEL}>Corners</span>
      <div className="grid grid-cols-5 gap-1" role="radiogroup" aria-label="Corners">
        {RADIUS_STEPS.map((step) => (
          <button
            key={step.step}
            type="button"
            role="radio"
            aria-checked={picked === step.step}
            title={step.label}
            className={`flex h-9 flex-col items-center justify-center gap-1 rounded-md border text-[10px] ${
              picked === step.step ? 'border-foreground bg-muted font-medium' : 'border-border hover:bg-muted/60'
            }`}
            onClick={() => choose(step)}
          >
            <span
              aria-hidden
              className="block h-3.5 w-3.5 border-l-2 border-t-2 border-foreground"
              style={{ borderTopLeftRadius: step.step === 'full' ? '9999px' : step.value }}
            />
            {step.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Row({
  label,
  unit,
  initial,
  onCommit,
  compact,
}: {
  label: string;
  unit: string;
  initial: string;
  onCommit: (value: string) => void;
  compact?: boolean;
}): React.ReactElement {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return (
    <label className={compact ? 'block' : 'flex items-center justify-between gap-2'}>
      <span className={compact ? 'text-[10px] text-muted-foreground' : LABEL}>{label}</span>
      <span className="flex items-center gap-1">
        <Input
          className={compact ? 'h-7 text-xs' : `${FIELD} w-20 text-right`}
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          onBlur={() => {
            if (value !== initial && value.trim() !== '') onCommit(value.trim());
          }}
        />
        {unit && !compact ? <span className="text-[10px] text-muted-foreground">{unit}</span> : null}
      </span>
    </label>
  );
}
